import { mapGetters } from 'vuex';
import { getStaticStreetPath, getStaticMapPath } from '../../../../widget/spec/mapHelper';
import { loadImg as nativeLoadImg } from '@/common/utilities/nojq/_load.img.js';
import { hasStreetView } from '@/common/utilities/nojq/_load.map.js';

function setImg(img, src) {
    nativeLoadImg(img, src);
}
export default {
    name: 'mvtMapStatic',
    inject: ['$eventBus'],
    props: {
        height: {
            default: 360,
        },
        width: {
            default: 0,
        },
        type: {
            default: 'terrain',
        },
        streetViewDefautType: {
            default: 'satellite',
        },
        address: {
            default: '',
        },
        icon: {
            default: 'icon-property-single-family',
        },
        lat: {
            default: 0,
        },
        lng: {
            default: 0,
        },
        zoom: {
            default: 15,
        },
        markers: {
            default() {
                return [];
            },
        },
        autofit: {
            default: true,
        },
        cacheFirst: {
            default: false,
        },
        resolution: {
            default: 0,
        },
        index: {
            default: 0,
        },
        placeholder: {
            default: '',
        },
        alt: {
            default: 'Map Image',
        },
        anchor: {
            default: '',
        },
        eager: {
            default: false,
        },
        hideDefaultMarker: {
            default: false,
        },
    },
    watch: {
        markers: function () {
            if (this._mapInitialized) {
                this.loadMapSrc(this.address, this.lat, this.lng, this.markers);
            }
        },
    },
    data() {
        return {
            _address: '',
            _lng: null,
            _lat: null,
            _markers: [],
            _width: 0,
            _height: 0,
            fallBackStreetView: null,
        };
    },
    methods: {
        _getScrollParent(el) {
            let parent = el.parentElement;
            while (parent) {
                const style = window.getComputedStyle(parent);
                const overflowX = style.overflowX;
                if (overflowX === 'auto' || overflowX === 'scroll') {
                    return parent;
                }
                parent = parent.parentElement;
            }
            return null;
        },
        removeEventListeners() {
            this.$refs.img.removeEventListener('error', this.onMapImgError);
            this.$refs.img.removeEventListener('load', this.onMapImgLoaded);
        },
        isPlaceholderImg() {
            const img = this.$refs.img;
            return !img || !img.src || img.src.startsWith('data:');
        },
        isSsrPlaceholderImg() {
            return !!this.placeholder && this.$refs.img?.src === this.placeholder;
        },
        emitUrlLoaded(src) {
            this.$emit('urlLoaded', { src, src_l: src, index: this.index });
        },
        onMapImgError() {
            if (this.isPlaceholderImg()) return;
            this.$eventBus.$emit('ga', { en: 'map', ec: 'error', el: this.type });
            this.fallBackStreetView = true;
            if (this.streetViewDefautType) {
                this.initialNormalMapView(this.streetViewDefautType);
            }
            this.$emit('error');
            this.removeEventListeners();
        },
        onMapImgLoaded() {
            if (this.isPlaceholderImg()) return;
            this.fallBackStreetView = false;
            // Fetched map URLs are reported by initialStreetView/initialNormalMapView;
            // only the SSR placeholder has no other emit path.
            if (this.isSsrPlaceholderImg()) {
                this.emitUrlLoaded(this.$refs.img.src);
            }
            this.removeEventListeners();
        },
        onMountedImgPrecheck() {
            if (this.fallBackStreetView !== null) return;
            const img = this.$refs.img;
            if (!img) return;
            // SSR can start loading before hydration attaches listeners.
            // If loading already failed, replay the same error flow.
            if (img.complete && img.currentSrc && !this.isPlaceholderImg()) {
                if (!img.naturalWidth || img.naturalWidth < 10) {
                    this.onMapImgError();
                } else {
                    this.onMapImgLoaded();
                }
            } else {
                this.$refs.img.addEventListener('error', this.onMapImgError);
                this.$refs.img.addEventListener('load', this.onMapImgLoaded);
            }
        },
        initMapSize() {
            if (this.resolution === 0) {
                this._resolution = Math.min(window.devicePixelRatio || 1, 2);
            }
            if (this.autofit && this.$el.offsetWidth > 0) {
                this._width = this.$el.offsetWidth;
                if (this.$el.offsetHeight > 0) {
                    this._height = this.$el.offsetHeight;
                }
            } else if (!this.width) {
                this._width = this.$el.offsetWidth || 480;
            }
        },
        initMap() {
            if (this._mapInitialized) return;
            this._mapInitialized = true;
            this.loadMapSrc(this.address, this.lat, this.lng, this.markers);
        },
        forceLoad() {
            if (this._visibilityObserver) {
                this._visibilityObserver.disconnect();
                this._visibilityObserver = null;
            }
            this.initMap();
        },
        getMapUrl(path) {
            var srcDfd = null,
                key = encodeURIComponent(this._address || this._lat + '_' + this._lng) + '_' + this.type;

            if (this.cacheFirst) {
                srcDfd = window.hashTable[key];
            }

            if (!srcDfd) {
                srcDfd = new Promise((resolve) => {
                    if (this.glb.isDevelopment) {
                        resolve(`https://maps.googleapis.com${path}`);
                    } else {
                        const url = `${this.glb.appUrl}webservices/getgooglemapurlwithsignature/`;
                        const body = { url: path };
                        fetch(url, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json; charset=UTF-8',
                            },
                            body: JSON.stringify(body),
                        })
                            .then((response) => response.json())
                            .then((data) => {
                                if (data.url) {
                                    resolve(`https://maps.googleapis.com${data.url}`);
                                }
                            });
                    }
                    window.hashTable[key] = srcDfd;
                });
            }
            return srcDfd;
        },
        setGSMapParams(type) {
            let params = {
                lat: this._lat || this.lat,
                lng: this._lng || this.lng,
                address: this._address,
                zoom: this.zoom || 15,
                width: this._width,
                height: this._height,
                markers: this._markers,
                markerCdnUrl: `${!this.glb.cdnUrl.includes(':3015/') ? this.glb.cdnUrl : 'https://www.movoto.com/novaimgs/'}`,
                type: type,
                id: this.glb.googleMapKey,
                resolution: this._resolution,
            };
            if (this.anchor) {
                params.anchor = this.anchor;
            }
            return getStaticMapPath(params);
        },
        setStreetViewParams() {
            let params = {
                lat: this._lat,
                lng: this._lng,
                address: this._address,
                width: this._width,
                height: this._height,
                id: this.glb.googleMapKey,
                resolution: this._resolution,
            };
            return getStaticStreetPath(params);
        },
        initialStreetView() {
            this.getMapUrl(this.setStreetViewParams()).then((src) => {
                setImg(this.$refs.img, src);
                this.$emit('urlLoaded', {
                    src: src,
                    src_l: src,
                    index: this.index,
                });
            });
        },
        initialNormalMapView(type) {
            this.initMapSize();
            this.getMapUrl(this.setGSMapParams(type)).then((src) => {
                setImg(this.$refs.img, src);
                this.$emit('urlLoaded', {
                    src: src,
                    src_l: src,
                    index: this.index,
                });
            });
        },
        loadMapSrc(address, lat, lng, markers) {
            if (address) {
                this._address = address;
            }
            if (lat && lng) {
                this._lat = lat;
                this._lng = lng;
            }
            if (markers) {
                this._markers = markers;
            }
            if (this.type === 'streetview') {
                hasStreetView({
                    address: this._address,
                    lat: this._lat,
                    lng: this._lng,
                    callback: (hasSteetView) => {
                        if (hasSteetView) {
                            this.initialStreetView();
                        } else if (this.streetViewDefautType) {
                            this.initialNormalMapView(this.streetViewDefautType);
                        } else {
                            this.$emit('error');
                        }
                    },
                });
            } else {
                this.initialNormalMapView(this.type);
            }
        },
    },
    computed: {
        ...mapGetters('glb', ['glb']),
        placeholderUrl() {
            return this.placeholder || 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
        },
        defaultCenterMarker() {
            if (this.hideDefaultMarker) return false;
            return this.fallBackStreetView || (this.type !== 'streetview' && this.markers.length === 0);
        },
        enableStreetViewFallback() {
            return this.type === 'streetview';
        },
    },
    beforeMount() {
        this._width = this.width || 480;
        this._height = this.height;
        this._resolution = this.resolution || 1;
    },
    mounted() {
        if (this.enableStreetViewFallback) {
            this.onMountedImgPrecheck();
        }
        if (this.placeholderUrl && !this.placeholderUrl.startsWith('data:')) {
            this._mapInitialized = true;
            if (!this.enableStreetViewFallback) {
                this.$emit('urlLoaded', {
                    src: this.placeholder,
                    src_l: this.placeholder,
                    index: this.index,
                });
            }
            return;
        }

        if (this.eager) {
            this.initMap();
            return;
        }

        const setupObserver = () => {
            if (this._mapInitialized) return;
            if (window.IntersectionObserver) {
                const scrollRoot = this._getScrollParent(this.$el);
                const rootMargin = scrollRoot ? '200px 0px 200px 0px' : '200px';
                this._visibilityObserver = new IntersectionObserver((entries) => {
                    for (const entry of entries) {
                        if (entry.isIntersecting) {
                            this._visibilityObserver.disconnect();
                            this._visibilityObserver = null;
                            this.initMap();
                            break;
                        }
                    }
                }, { root: scrollRoot, rootMargin, threshold: 0.01 });
                this._visibilityObserver.observe(this.$el);
            } else {
                this.initMap();
            }
        };
        requestAnimationFrame(setupObserver);
    },
    beforeUnmount() {
        this.removeEventListeners();
        if (this._visibilityObserver) {
            this._visibilityObserver.disconnect();
            this._visibilityObserver = null;
        }
    },
};
